import { ContainedNumberField } from 'components/Form/FinalForm/Contained/ContainedNumberField';
import { useField, useForm } from 'react-final-form';
import { useEstimateCarPriceMutation } from 'services/api/upload-vehicle';
import { normalizeFormattedField } from 'utils';
import { required } from 'utils/validators';
import { SubmitAction } from './ui';
import { ErrorStyle } from 'components/Form/FinalForm/Contained/Error';
import { useDeviceInfo } from 'hooks/useDeviceInfo';

type Props = {
  vehicleId: number;
  errorStyle?: ErrorStyle;
  disabled?: boolean;
  onMileageFetched?: () => void;
};

export const MileageFields = ({ vehicleId, disabled, onMileageFetched, errorStyle = 'default' }: Props) => {
  const { isMobile } = useDeviceInfo()
  
  const {
    input: { value: bluebook_mileage_adjustment }
  } = useField('bluebook_mileage_adjustment', { subscription: { value: true } });
  const {
    input: { value: mileage }
  } = useField('mileage', { subscription: { value: true } });

  const {
    input: { value: uploadType }
  } = useField('upload_type', { subscription: { value: true } });

  const {
    input: { value: options }
  } = useField('options', { subscription: { value: true } });

  const {
    input: { value: trim }
  } = useField('trim', { subscription: { value: true } });

  const {
    input: { value: driveTrain }
  } = useField('drive_train', { subscription: { value: true } });

  const {
    input: { value: engine }
  } = useField('engine', { subscription: { value: true } });

  const {
    input: { value: transmission }
  } = useField('transmission', { subscription: { value: true } });

  const [estimateCarPrice] = useEstimateCarPriceMutation();

  const hasMileage = uploadType === 'manual' || !!bluebook_mileage_adjustment;
  const hasInputMileage = mileage && Number(mileage) > 0;

  const form = useForm();

  const hasTrimFields = trim?.value && driveTrain?.value && engine?.value && transmission?.value;

  const getOrUpdateMileageValues = async () => {
    if (!hasInputMileage) return;
    const selectedBlueBookOptions = (options || []).filter(o => o.is_typical).map(o => o.bluebook_option_id);
    const response = await estimateCarPrice({
      vehicleId,
      mileage: mileage,
      config: {
        options: selectedBlueBookOptions,
        trim: trim?.value,
        drive_train: driveTrain?.value,
        engine: engine?.value,
        transmission: transmission?.value
      }
    });
    if ('data' in response && response.data.data) {
      const newValues = response.data.data;
      form.batch(() => {
        form.change('bluebook_auction_value_good', newValues.bluebook_auction_value_good);
        form.change('bluebook_mileage', newValues.bluebook_mileage);
        form.change('bluebook_mileage_adjustment', newValues.bluebook_mileage_adjustment);
        form.change('bluebook_value', newValues.bluebook_value);
        form.change('mileage', newValues.mileage);
      });

      if (onMileageFetched) {
        onMileageFetched();
      }
    }
  };

  return (
    <>
      <ContainedNumberField
        validate={required}
        name="mileage"
        label="Enter Mileage"
        instantError={isMobile}
        inputMode="numeric"
        disabled={disabled}
        errorStyle={errorStyle}
        numberFormatProps={{
          allowNegative: false,
          thousandSeparator: true,
          decimalScale: 0
        }}
        parse={normalizeFormattedField}
      />
      {hasMileage && (
        <ContainedNumberField
          validate={required}
          errorStyle={errorStyle}
          name="bluebook_value"
          inputMode="numeric"
          label="Value Without Miles Adj"
          numberFormatProps={{
            allowNegative: false,
            thousandSeparator: true,
            decimalScale: 0,
            prefix: '$'
          }}
          parse={normalizeFormattedField}
          disabled={disabled}
        />
      )}
      {hasMileage && (
        <ContainedNumberField
          validate={required}
          errorStyle={errorStyle}
          name="bluebook_mileage"
          label="Blue Book® Lending Value"
          inputMode="numeric"
          numberFormatProps={{
            allowNegative: false,
            thousandSeparator: true,
            decimalScale: 0,
            prefix: '$'
          }}
          parse={normalizeFormattedField}
          disabled={disabled}
        />
      )}
      {uploadType !== 'manual' && (
        <SubmitAction
          disabled={!hasInputMileage || disabled || !hasTrimFields}
          black
          variant="contained"
          color="secondary"
          type="button"
          onClick={getOrUpdateMileageValues}
        >
          {!hasMileage ? 'Get Values' : 'Update Values'}
        </SubmitAction>
      )}
    </>
  );
};
